/** @format */

import {
  api,
  getLS,
  sendErrorToSentry,
  getServiceConfig,
} from 'utility';
import db from 'utility/DexieDb';
import { getPermissionUrl } from 'URLs';
import AuthApi from './Api';
import createToast from 'components/Toast/Toast';

/**
 * تبدیل داده خطای Blob یا ArrayBuffer به شیء JSON یا متن
 */
async function parseErrorResponseData(data) {
  if (!data) {
    return data;
  }

  if (data instanceof Blob) {
    const text = await data.text();

    if (!text) {
      return data;
    }

    try {
      return JSON.parse(text);
    } catch {
      return text;
    }
  }

  if (data instanceof ArrayBuffer) {
    const text = new TextDecoder().decode(data);

    try {
      return JSON.parse(text);
    } catch {
      return text;
    }
  }

  return data;
}

export class AuthService {
  TOKEN_KEY_NAME = 'TOKEN';
  REFRESH_TOKEN_KEY = 'LATEST_REFRESH_TOKEN';
  axios;
  refreshTokenPromise = null;
  isRedirectingToLogin = false;

  constructor() {
    this.axios = api.axios;
    this.configAxios();
  }

  get token() {
    return this.TOKEN_KEY_NAME;
  }

  getLoginBusinessUrl = async (params) => {
    return AuthApi.getLoginBusinessUrl(params);
  };

  getLoginUrl = async (params) => {
    return AuthApi.getLoginUrl(params);
  };

  getLoginOTP = async (params) => {
    return AuthApi.getLoginOTP(params);
  };

  getLastLogin = async () => {
    return AuthApi.getLastLogin();
  };

  fetchToken = async (params) => {
    return AuthApi.fetchToken(params).then((res) => {
      const env = process.env?.REACT_APP_ENVIRONMENT;
      const data = res.data.result;

      const tokenData = {
        token: data.panelToken,
      };

      if (env === 'development') {
        this.saveToLS(tokenData);
        return data.panelToken;
      }

      if (data.eligible) {
        this.saveToLS(tokenData);
        return data.panelToken;
      }

      return false;
    });
  };

  isAuthenticated = () => {
    const token = getLS(this.TOKEN_KEY_NAME);

    return !!token;
  };

  fetchBusiness() {
    return AuthApi.fetchBusiness();
  }

  logout = async (refreshTokenExpired) => {
    const loc = window.location.pathname;

    try {
      const appURL = process.env.REACT_APP_REDIRECT_URI;
      const res = await AuthApi.revoke(appURL);

      return res;
    } catch (error) {
      await this.clearStorage();

      if (refreshTokenExpired) {
        this.setLastUserLocation(loc);
      }

      window.location.href = './sign-in';

      throw error;
    }
  };

  revokeLogin = async () => {
    try {
      const appURL = process.env.REACT_APP_REDIRECT_URI;
      const res = await AuthApi.revoke(appURL);

      return res;
    } catch (error) {
      return null;
    }
  };

  clearToken = () => {
    localStorage.clear();

    return db.delete();
  };

  clearStorage = async () => {
    if (localStorage.getItem('fileHashCode')) {
      localStorage.removeItem(this.REFRESH_TOKEN_KEY);
      localStorage.removeItem('panel:redirect');
      localStorage.removeItem(this.TOKEN_KEY_NAME);

      return;
    }

    await this.clearToken();
  };

  /**
   * تمام درخواست‌های refresh از این متد عبور می‌کنند.
   *
   * اگر چند درخواست هم‌زمان 401 دریافت کنند،
   * فقط اولین درخواست refresh ارسال می‌شود و بقیه
   * منتظر همان Promise می‌مانند.
   */
  refreshTokenOnce = () => {
    if (!this.refreshTokenPromise) {
      this.refreshTokenPromise = this.refreshToken().finally(() => {
        this.refreshTokenPromise = null;
      });
    }

    return this.refreshTokenPromise;
  };

  /**
   * دریافت Service Config
   *
   * اگر getServiceConfig خطای 401 بگیرد:
   * 1. توکن refresh می‌شود.
   * 2. getServiceConfig مجدداً اجرا می‌شود.
   */
  getServiceConfigWithRefresh = async () => {
    try {
      return await getServiceConfig({
        skipServiceConfig: true,
        skipAuthRefresh: true,
        ignoreCancelToken: true,
        noToast: true,
      });
    } catch (error) {
      const status = error?.response?.status;

      if (status !== 401) {
        throw error;
      }

      await this.refreshTokenOnce();

      return getServiceConfig({
        skipServiceConfig: true,
        skipAuthRefresh: true,
        ignoreCancelToken: true,
        noToast: true,
      });
    }
  };

  /**
   * هدایت به صفحه ورود بعد از شکست refresh token
   */
  redirectToLogin = async () => {
    if (this.isRedirectingToLogin) {
      return;
    }

    this.isRedirectingToLogin = true;

    try {
      await this.clearStorage();
      this.removeLastUserLocation();
    } finally {
      window.location.href = './sign-in';
    }
  };

  configAxios = () => {
    const pendingRequests = new Map();

    const generateRequestKey = (config = {}) => {
      const { method, url, params, data } = config;

      return [
        method,
        url,
        JSON.stringify(params),
        JSON.stringify(data),
      ].join('&');
    };

    const cancelDuplicateRequest = (config) => {
      const requestKey = generateRequestKey(config);

      if (pendingRequests.has(requestKey)) {
        const controller = pendingRequests.get(requestKey);

        controller.abort();
        pendingRequests.delete(requestKey);
      }
    };

    const addPendingRequest = (config) => {
      const requestKey = generateRequestKey(config);
      const controller = new AbortController();

      config.signal = controller.signal;
      config.cancelTokenController = controller;

      pendingRequests.set(requestKey, controller);
    };

    const removePendingRequest = (config) => {
      if (!config) {
        return;
      }

      const requestKey = generateRequestKey(config);

      pendingRequests.delete(requestKey);
    };

    this.cancelAllPendingRequests = () => {
      for (const [key, controller] of pendingRequests.entries()) {
        controller.abort();
        pendingRequests.delete(key);
      }
    };

    /**
     * Request interceptor
     */
    this.axios.interceptors.request.use(
      async (config) => {
        if (!config.ignoreCancelToken) {
          cancelDuplicateRequest(config);
          addPendingRequest(config);
        }

        const url = config.url || '';

        /**
         * فقط /report و مسیرهای زیرمجموعه آن
         */
        const isReport = /^\/?report(?:\/|$)/i.test(url);

        let serviceConfig = {};

        /**
         * درخواست‌های service config و refresh token
         * نباید مجدداً getServiceConfig را اجرا کنند.
         */
        if (!config.skipServiceConfig) {
          try {
            const serviceConfigResponse =
              await this.getServiceConfigWithRefresh();

            /**
             * پشتیبانی از هر سه ساختار:
             *
             * 1. { baseUrl, reportBaseUrl }
             * 2. { result: { baseUrl, reportBaseUrl } }
             * 3. Axios response: { data: { result: ... } }
             */
            const responseData =
              serviceConfigResponse?.data ?? serviceConfigResponse ?? {};

            serviceConfig = responseData?.result ?? responseData ?? {};
          } catch (error) {
            /**
             * اگر refresh شکست خورده باشد، کاربر logout می‌شود.
             */
            if (error?.response?.status === 401) {
              await this.redirectToLogin();
            }

            if (!config.ignoreCancelToken) {
              removePendingRequest(config);
            }

            return Promise.reject(error);
          }
        }

        const fallbackBaseUrl = process.env?.REACT_APP_API_JAVA
          ? `${process.env.REACT_APP_API_JAVA}${
              process.env.REACT_APP_BASE_URL || ''
            }`
          : process.env.REACT_APP_BASE_URL || '';

        /**
         * report => reportBaseUrl
         * سایر درخواست‌ها => baseUrl
         */
        config.baseURL = isReport
          ? serviceConfig.reportBaseUrl || fallbackBaseUrl
          : serviceConfig.baseUrl || fallbackBaseUrl;

        config.headers = config.headers || {};

        const baseUrlOwner = process.env?.REACT_APP_REDIRECT_OWNER_URI;

        const shouldAttachToken =
          url.startsWith('/') ||
          (baseUrlOwner && url.startsWith(baseUrlOwner));

        if (shouldAttachToken && !config.skipAuthorization) {
          const token = getLS(this.TOKEN_KEY_NAME);

          if (token) {
            config.headers.Authorization = `Bearer ${token}`;
          }
        }

        return config;
      },
      (error) => Promise.reject(error),
    );

    /**
     * Response interceptor
     */
    this.axios.interceptors.response.use(
      (response) => {
        if (!response.config?.ignoreCancelToken) {
          removePendingRequest(response.config);
        }

        return response;
      },

      async (error) => {
        const originalConfig = error?.config;
        const status = error?.response?.status;

        if (originalConfig && !originalConfig.ignoreCancelToken) {
          removePendingRequest(originalConfig);
        }

        /**
         * درخواست cancel شده نباید toast یا refresh ایجاد کند.
         */
        if (
          error?.name === 'CanceledError' ||
          error?.name === 'AbortError' ||
          error?.code === 'ERR_CANCELED'
        ) {
          return Promise.reject(error);
        }

        /**
         * مدیریت عمومی خطای 401
         */
        if (
          status === 401 &&
          originalConfig &&
          !originalConfig._retry &&
          !originalConfig.skipAuthRefresh
        ) {
          originalConfig._retry = true;

          return this.refreshTokenAndRetry(error);
        }

        // تبدیل داده خطا در صورتی که Blob یا ArrayBuffer باشد
        const parsedData = await parseErrorResponseData(
          error?.response?.data,
        );

        if (error?.response) {
          error.response.data = parsedData;
        }

        const serviceMessage =
          parsedData?.message?.fa ||
          parsedData?.message ||
          (typeof parsedData === 'string' && parsedData.trim()
            ? parsedData
            : null);

        const message =
          serviceMessage ||
          (status === 500
            ? 'Server communication error'
            : 'Error performing operation');

        /**
         * این سرویس‌ها در هیچ شرایطی نباید toast نمایش دهند:
         * 1. log/feature
         * 2. oauth2/revoke
         */
        const requestUrl = originalConfig?.url || '';
        const isMutedService =
          /(?:^|\/)(?:log\/feature|oauth2\/revoke)(?:\/|$|\?)/i.test(
            requestUrl,
          );

        const hasIdToken = originalConfig?.headers?.idToken;
        const shouldSuppressToast =
          originalConfig?.noToast || originalConfig?.noToastStatuses?.includes(status);

        sendErrorToSentry(AuthApi.businessData, error);

        /**
         * خطای 504 در هیچ شرایطی نباید toast نمایش دهد.
         * خطا همچنان برای مدیریت loading و سایر رفتارهای محلی reject می‌شود.
         */
        if (status === 504) {
          return Promise.reject(error);
        }

        /**
         * خطای 502:
         * - در صورت وجود message سرویس، همان نمایش داده می‌شود.
         * - در غیر این صورت پیام پیش‌فرض نمایش داده می‌شود.
         * - سرویس‌های muted هرگز toast ندارند.
         */
        if (status === 502) {
          if (!isMutedService && !shouldSuppressToast) {
            const gatewayErrorMessage =
              serviceMessage ||
              'خطا در برقراری ارتباط با سرور. لطفاً دقایقی دیگر دوباره تلاش کنید.';

            createToast('Error', gatewayErrorMessage, 'danger');
          }

          return Promise.reject(error);
        }

        /**
         * نمایش toast عمومی برای سایر خطاها
         */
        if (!isMutedService && !shouldSuppressToast && !hasIdToken) {
          createToast('Error', message, 'danger');
        }

        if (status === 403 && parsedData) {
          return Promise.reject(parsedData);
        }

        return Promise.reject(error);
      },
    );
  };

  /**
   * Refresh token و اجرای مجدد درخواست قبلی
   */
  refreshTokenAndRetry = async (error) => {
    const originalConfig = error?.config;

    if (!originalConfig) {
      return Promise.reject(error);
    }

    try {
      await this.refreshTokenOnce();

      /**
       * Authorization قبلی حذف می‌شود تا request interceptor
       * توکن جدید را از localStorage بردارد.
       */
      if (originalConfig.headers) {
        delete originalConfig.headers.Authorization;
        delete originalConfig.headers.authorization;
      }

      return this.axios(originalConfig);
    } catch (refreshError) {
      await this.redirectToLogin();

      return Promise.reject(refreshError);
    }
  };

  getLastUserLocation = () => {
    try {
      return localStorage.getItem('last-location');
    } catch (error) {
      return null;
    }
  };

  setLastUserLocation = (loc) => {
    try {
      const currentLoc = loc ?? window.location.pathname;

      localStorage.setItem('last-location', `${currentLoc}`);

      return currentLoc;
    } catch (error) {
      return null;
    }
  };

  removeLastUserLocation = () => {
    try {
      localStorage.removeItem('last-location');
    } catch (error) {
      // localStorage may be unavailable
    }
  };

  refreshToken = async () => {
    const token = getLS(this.TOKEN_KEY_NAME);

    /**
     * پارامتر دوم باید در AuthApi.refreshToken پشتیبانی شود.
     * این flagها مانع recursion و refresh loop می‌شوند.
     */
    const result = await AuthApi.refreshToken(token, {
      skipServiceConfig: true,
      skipAuthRefresh: true,
      ignoreCancelToken: true,
      noToast: true,
    });

    const credentials = result?.data?.result ?? result?.data ?? result;

    this.saveToLS(credentials);

    return result;
  };

  saveToLS = (credentials = {}) => {
    const token =
      credentials.token ||
      credentials.panelToken ||
      credentials?.result?.panelToken ||
      credentials?.result?.token;

    if (!token) {
      throw new Error('Token was not found in refresh response');
    }

    localStorage.setItem(this.TOKEN_KEY_NAME, token);
  };

  getPermission = async () => {
    return api.get(getPermissionUrl()).then((res) => res.data);
  };
}

const authService = new AuthService();

export default authService;
